/**
 * SEO Sprint 4B (Feb 2026) — FAQ Categories section.
 * SEO Sprint 4C (Feb 2026) — client-side zoekfilter.
 *
 * Renders all 11 FAQ-categories on /faq with collapsible accordion
 * items. Data komt uit `GET /api/website/faq-categories` (admin-
 * beheerbaar via Websitebeheer → FAQ-categorieën).
 *
 * Schema-policy (4B): FAQPage JSON-LD wordt opgebouwd uit ALLE 77
 * vragen — ongeacht zoekfilter. Hierdoor blijft het schema stabiel
 * voor Google en hoeven we geen tweede schema-bron te onderhouden.
 *
 * Search behaviour (4C):
 *   - Filter is case- en accent-insensitief (NFD-normalize)
 *   - Matcht op vraag, antwoord én categorie-titel
 *   - Categorieën zonder matches worden verborgen
 *   - Items binnen een matchende categorie die zelf niet matchen
 *     worden verborgen
 *   - Bij actieve query: categorieën standaard auto-open (via
 *     `open`-attribute) zodat alle treffers direct zichtbaar zijn
 *   - `onCounts` callback meldt aan de parent: {total, matching}
 *
 * Accordion uses native <details>/<summary> — no extra deps, full
 * accessibility (keyboard + screenreader).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronDown } from "lucide-react";
import Schema from "../Schema";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function _normalize(s) {
  if (!s) return "";
  return String(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function _itemMatches(item, normQuery) {
  if (!normQuery) return true;
  const haystack = _normalize(`${item.question || ""} ${item.answer || ""}`);
  return haystack.includes(normQuery);
}

function _categoryMatches(cat, normQuery) {
  if (!normQuery) return true;
  // Match on category title (e.g. "betalen" → category "Betaling") OR
  // when at least one item inside the category matches.
  if (_normalize(cat.title).includes(normQuery)) return true;
  return (cat.items || []).some((it) => _itemMatches(it, normQuery));
}

export default function FAQCategorySection({
  testid = "faq-categories",
  searchQuery = "",
  onCounts,
}) {
  const [cats, setCats] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/website/faq-categories`, { timeout: 8000 })
      .then((res) => {
        if (cancelled) return;
        const next = res?.data?.categories;
        if (Array.isArray(next)) setCats(next);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // ----- Schema items (always all 77 — never filtered) ---------------
  const allItems = useMemo(() => (
    cats.flatMap((c) =>
      (c.items || []).map((it) => ({
        question: it.question, answer: it.answer,
      })),
    ).filter((i) => i.question && i.answer)
  ), [cats]);

  // ----- Filtered view (Sprint 4C) ----------------------------------
  const normQuery = _normalize(searchQuery);
  const filteredCats = useMemo(() => {
    if (!normQuery) return cats;
    const out = [];
    for (const c of cats) {
      const titleHits = _normalize(c.title).includes(normQuery);
      const matchingItems = (c.items || []).filter((it) => _itemMatches(it, normQuery));
      // Show category if title matches (then show all items) or if some
      // items match (then only show matching items).
      if (titleHits) {
        out.push({ ...c, items: c.items, _autoOpen: true });
      } else if (matchingItems.length > 0) {
        out.push({ ...c, items: matchingItems, _autoOpen: true });
      }
    }
    return out;
  }, [cats, normQuery]);

  // Report counts upward so the search bar can render "x of y" feedback.
  const totalItems = allItems.length;
  const matchingItems = useMemo(() => (
    filteredCats.reduce((n, c) => n + (c.items?.length || 0), 0)
  ), [filteredCats]);
  useEffect(() => {
    if (typeof onCounts === "function") {
      onCounts({ total: totalItems, matching: matchingItems });
    }
  }, [totalItems, matchingItems, onCounts]);

  // ----- Render ------------------------------------------------------
  if (!loaded && cats.length === 0) {
    return (
      <div className="py-10 text-center text-gray-400 text-sm" data-testid={`${testid}-loading`}>
        FAQ inhoud laden...
      </div>
    );
  }

  return (
    <section
      className="bg-white py-12 sm:py-16"
      data-testid={testid}
      id="faq-categories"
    >
      {allItems.length > 0 ? (
        <Schema type="FAQPage" data={{ items: allItems }} />
      ) : null}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Empty-state: search query but no results */}
        {normQuery && filteredCats.length === 0 ? (
          <FAQNoResults query={searchQuery} testid={`${testid}-no-results`} />
        ) : (
          <div className="space-y-12 sm:space-y-14">
            {filteredCats.map((c, idx) => (
              <FAQCategoryBlock
                key={c.key}
                category={c}
                index={idx + 1}
                forceOpen={!!c._autoOpen}
                testid={`${testid}-${c.key}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FAQCategoryBlock({ category, index, forceOpen, testid }) {
  const items = Array.isArray(category.items) ? category.items : [];
  if (items.length === 0) return null;
  return (
    <div data-testid={testid} className="scroll-mt-24" id={`faq-cat-${category.key}`}>
      <div className="mb-5 flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#082c50] text-white text-sm font-black flex-shrink-0">
          {index}
        </span>
        <div className="flex-1">
          <h2 className="font-chivo font-bold text-xl sm:text-2xl text-[#082c50] tracking-tight">
            {category.title}
          </h2>
          {category.intro ? (
            <p className="text-sm text-[#514e4e] mt-1.5">{category.intro}</p>
          ) : null}
        </div>
      </div>
      <ul className="space-y-3" data-testid={`${testid}-items`}>
        {items.map((it, i) => (
          <li
            key={i}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden"
            data-testid={`${testid}-item-${i}`}
          >
            <details className="group" {...(forceOpen ? { open: true } : {})}>
              <summary className="flex items-center justify-between gap-4 p-4 sm:p-5 cursor-pointer list-none">
                <span className="font-semibold text-[#082c50] text-sm sm:text-base pr-2">
                  {it.question}
                </span>
                <ChevronDown
                  className="w-5 h-5 text-[#1a5193] flex-shrink-0 transition-transform group-open:rotate-180"
                  aria-hidden="true"
                />
              </summary>
              <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-sm text-[#514e4e] leading-relaxed whitespace-pre-line">
                {it.answer}
              </div>
            </details>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FAQNoResults({ query, testid }) {
  return (
    <div
      className="bg-slate-50 border border-slate-200 rounded-2xl p-8 sm:p-10 text-center"
      data-testid={testid}
    >
      <h3 className="font-chivo font-bold text-xl text-[#082c50] tracking-tight">
        Geen direct antwoord gevonden voor "{query.trim()}"
      </h3>
      <p className="mt-2 text-sm text-[#514e4e]">
        Probeer een ander zoekwoord, of neem contact op met 5999Cargo —
        onze medewerkers helpen u graag verder.
      </p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        <a
          href="/contact"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#082c50] text-white text-sm font-semibold hover:bg-[#0a335c] transition-colors"
          data-testid={`${testid}-contact`}
        >
          Contact opnemen
        </a>
        <a
          href="/contact?get-quote=1#quote-form"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[#082c50] text-[#082c50] text-sm font-semibold hover:bg-slate-50 transition-colors"
          data-testid={`${testid}-quote`}
        >
          Offerte aanvragen
        </a>
      </div>
    </div>
  );
}
